import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider, useAuth, useUser } from '@clerk/clerk-react';
import App from './App';
import { meetApi, setAuthTokenProvider } from './services/meetApi';
import { Storage, clearOnSignOut } from './lib/storage';
import './index.css';

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Hands Clerk's getToken() to the API client so every outgoing fetch
// carries an Authorization: Bearer <jwt> header when the user is signed in.
// Returns null when signed out — server treats it as anonymous.
//
// Also fires the one-time auto-link sweep that attaches any pre-existing,
// unclaimed-by-account badges whose stored profile email matches the user's
// verified Clerk email. We track the user id we already swept in
// localStorage so it doesn't run on every sign-in.
function ApiAuthBridge() {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    if (!isSignedIn) {
      setAuthTokenProvider(null);
      // Don't leave PII (cached profiles, form drafts, pending scans) in
      // localStorage after sign-out — matters on shared/kiosk devices.
      // The list of purged namespaces lives in src/lib/storage.js.
      clearOnSignOut();
      return;
    }
    setAuthTokenProvider(() => getToken());
    return () => setAuthTokenProvider(null);
  }, [isSignedIn, getToken]);

  useEffect(() => {
    if (!isSignedIn || !user?.id) return;
    if (Storage.autolinkDone.get() === user.id) return;
    // Defer one tick so the token provider above is registered first.
    const timer = setTimeout(async () => {
      try {
        const result = await meetApi.autoLinkBadges();
        Storage.autolinkDone.set(user.id);
        if (result?.linked?.length) {
          console.info(`[autolink] linked ${result.linked.length} existing badge(s) to your account:`, result.linked);
        }
      } catch (err) {
        // Don't burn the marker on failure — let the next sign-in retry.
        console.warn('[autolink] failed', err.message);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isSignedIn, user?.id]);

  return null;
}

function Root() {
  if (!PUBLISHABLE_KEY) {
    if (import.meta.env.DEV) {
      console.warn('[MEET] VITE_CLERK_PUBLISHABLE_KEY not set — auth UI disabled');
    }
    return <App />;
  }
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
      <ApiAuthBridge />
      <App />
    </ClerkProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);
